import { useSnackbar } from '@components/feedback/GlobalSnackbar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiError } from '@hooks/useApiError';
import { setAccessToken } from '@lib/axios';
import { LockOutlined, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import { CARD, FIELD, LABEL, SAVE_BTN, TITLE } from '../styles';
import { useChangePassword } from '../useSettingsData';

const pwdSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword:     z.string().min(6, 'Must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Password do not match',
    path: ['confirmPassword'],
  });

type PwdValues = z.infer<typeof pwdSchema>;

export const ChangePasswordCard = () => {
  const { show: showSnack } = useSnackbar();
  const changePassword = useChangePassword();

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register: regPwd,
    handleSubmit: handlePwdSubmit,
    reset: resetPwd,
    formState: { errors: pwdErrors, isSubmitting: isPwdSubmitting },
    setError: setPwdError,
  } = useForm<PwdValues>({ resolver: zodResolver(pwdSchema) });

  const onChangePwd = async (values: PwdValues) => {
    try {
      const { data } = await changePassword.mutateAsync(values);
      setAccessToken((data as { accessToken: string }).accessToken);
      showSnack('Password changed successfully.', 'success');
      resetPwd();
      setShowCurrent(false); setShowNew(false); setShowConfirm(false);
    } catch (err) {
      setPwdError('root', { message: useApiError(err) ?? 'Failed to change password.' });
    }
  };

  return (
    <Box sx={CARD} component="form" onSubmit={handlePwdSubmit(onChangePwd)}>
      <Typography sx={{ ...TITLE, mb: '20px' }}>Change Password</Typography>

      {pwdErrors.root?.message && (
        <Alert severity="error" sx={{ mb: '16px', fontSize: 13, borderRadius: '10px' }}>
          {pwdErrors.root.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mb: '20px' }}>
        {(
          [
            { field: 'currentPassword' as const, label: 'Current Password',     show: showCurrent, toggle: () => setShowCurrent((v) => !v), placeholder: 'Enter current password' },
            { field: 'newPassword'     as const, label: 'New Password',          show: showNew,     toggle: () => setShowNew((v) => !v),     placeholder: 'Enter new password (min 6 chars)' },
            { field: 'confirmPassword' as const, label: 'Confirm New Password',  show: showConfirm, toggle: () => setShowConfirm((v) => !v), placeholder: 'Confirm new password' },
          ]
        ).map(({ field, label, show, toggle, placeholder }) => (
          <Box key={field}>
            <Typography component="label" sx={LABEL}>{label}</Typography>
            <TextField
              type={show ? 'text' : 'password'}
              fullWidth
              placeholder={placeholder}
              error={!!pwdErrors[field]}
              helperText={pwdErrors[field]?.message}
              {...regPwd(field)}
              sx={FIELD}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={(theme) => ({ fontSize: 18, color: theme.palette.grey[400] })} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={toggle} edge="end" tabIndex={-1}>
                        {show
                          ? <VisibilityOffOutlined sx={(theme) => ({ fontSize: 18, color: theme.palette.grey[400] })} />
                          : <VisibilityOutlined   sx={(theme) => ({ fontSize: 18, color: theme.palette.grey[400] })} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" disabled={isPwdSubmitting} sx={SAVE_BTN}>
          {isPwdSubmitting
            ? <CircularProgress size={16} color="inherit" />
            : 'Change Password'}
        </Button>
      </Box>
    </Box>
  );
};
